import React from 'react'
import { Badge, BaseFileUpload, Button, Table, Td, Th, Tr } from '@/shared/ui'
import { uploadPublicFile } from '@/modules/upload/services/uploadService'
import { AffiliateMonthlySummaryRow, getAffiliateMonthlySummary } from '../services/affiliateBalanceService'
import { PayoutReceipt, getPayoutReceiptsByIds, markAffiliateMonthPaid, savePayoutReceipt } from '../services/affiliatePayoutService'

type Props = {
  affiliateUserId: string
  onPayoutCreated?: () => void
  showReceiptColumn?: boolean
  showActions?: boolean
  allowReceiptManage?: boolean
}

function cents(v?: number | null) {
  return `R$ ${(Number(v || 0) / 100).toFixed(2)}`
}

function monthLabel(year: number, month: number) {
  return `${month.toString().padStart(2, '0')}/${year}`
}

export default function AffiliateMonthlySummaryPanel({
  affiliateUserId,
  onPayoutCreated,
  showReceiptColumn = true,
  showActions = true,
  allowReceiptManage = true,
}: Props) {
  const [rows, setRows] = React.useState<AffiliateMonthlySummaryRow[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [updatingKey, setUpdatingKey] = React.useState<string | null>(null)
  const [receiptByPayoutId, setReceiptByPayoutId] = React.useState<Record<string, PayoutReceipt>>({})
  const [receiptUpdatingId, setReceiptUpdatingId] = React.useState<string | null>(null)
  const shouldLoadReceipts = showReceiptColumn
  const load = React.useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const summary = await getAffiliateMonthlySummary(affiliateUserId, 12)
      setRows(summary)

      if (shouldLoadReceipts) {
        const payoutIds = Array.from(
          new Set(summary.flatMap((r) => (Array.isArray(r.payout_ids) ? r.payout_ids : [])).filter(Boolean)),
        )
        if (payoutIds.length > 0) {
          const receipts = await getPayoutReceiptsByIds(affiliateUserId, payoutIds)
          setReceiptByPayoutId(receipts)
        } else {
          setReceiptByPayoutId({})
        }
      } else {
        setReceiptByPayoutId({})
      }
    } catch (e: any) {
      setError(e?.message || 'Falha ao carregar resumo mensal')
    } finally {
      setLoading(false)
    }
  }, [affiliateUserId, shouldLoadReceipts])

  React.useEffect(() => {
    if (affiliateUserId) load()
  }, [affiliateUserId, load])

  async function handleMarkPaid(row: AffiliateMonthlySummaryRow) {
    const key = `${row.year}-${row.month}`
    setUpdatingKey(key)
    setError(null)
    try {
      await markAffiliateMonthPaid(affiliateUserId, row.year, row.month)
      await load()
      if (onPayoutCreated) onPayoutCreated()
    } catch (e: any) {
      setError(e?.message || 'Falha ao atualizar status do mes')
    } finally {
      setUpdatingKey(null)
    }
  }

  async function handleUploadReceipt(row: AffiliateMonthlySummaryRow, file: File) {
    const payoutId = Array.isArray(row.payout_ids) && row.payout_ids.length > 0 ? row.payout_ids[0] : null
    if (!payoutId) return
    setReceiptUpdatingId(payoutId)
    setError(null)
    try {
      const folder = `affiliates/payout_receipts/${affiliateUserId}/${row.year}-${row.month.toString().padStart(2, '0')}`
      const { url, bucket, path } = await uploadPublicFile(file, folder)
      const receipt: PayoutReceipt = {
        url,
        bucket,
        path,
        content_type: file.type || null,
        size_bytes: typeof file.size === 'number' ? file.size : null,
      }
      await savePayoutReceipt(affiliateUserId, payoutId, receipt)
      setReceiptByPayoutId((prev) => ({ ...prev, [payoutId]: receipt }))
    } catch (e: any) {
      setError(e?.message || 'Falha ao salvar comprovante')
    } finally {
      setReceiptUpdatingId(null)
    }
  }

  async function handleRemoveReceipt(row: AffiliateMonthlySummaryRow) {
    const payoutId = Array.isArray(row.payout_ids) && row.payout_ids.length > 0 ? row.payout_ids[0] : null
    if (!payoutId) return
    setReceiptUpdatingId(payoutId)
    setError(null)
    try {
      await savePayoutReceipt(affiliateUserId, payoutId, null)
      setReceiptByPayoutId((prev) => {
        const next = { ...prev }
        delete next[payoutId]
        return next
      })
    } catch (e: any) {
      setError(e?.message || 'Falha ao remover comprovante')
    } finally {
      setReceiptUpdatingId(null)
    }
  }

  return (
    <div className="mt-8">
      <div className="mb-2 flex items-center justify-between">
        <div className="text-base font-medium">Resumo mensal de comissoes</div>
        <Button variant="outline" disabled={loading} onClick={load}>
          Atualizar
        </Button>
      </div>
      {error && (
        <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}
      <Table>
        <thead>
          <Tr>
            <Th>Mes</Th>
            <Th>A Maturar</Th>
            <Th>Maturado</Th>
            <Th>Qtd.</Th>
          <Th>Total</Th>
          <Th>Status</Th>
            {showReceiptColumn && <Th>Comprovante</Th>}
            {showActions && <Th className="w-44 text-right">Acoes</Th>}
          </Tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const key = `${r.year}-${r.month}`
            const toMatureCents = Number(r.pending_cents || 0)
            const maturedCents = Number(r.available_cents || 0) + Number(r.reserved_cents || 0)
            const paidCents = Number(r.paid_cents || 0)
            const displayMaturedCents = maturedCents > 0 ? maturedCents : paidCents
            const totalCents = toMatureCents + maturedCents
            const pendingCount = Number(r.pending_accounts || 0)
            const maturedCount = Number(r.matured_accounts || 0)
            const isPaid = maturedCents === 0 && paidCents > 0
            const statusLabel = maturedCents > 0 ? 'Pendente' : isPaid ? 'Pago' : '-'
            const statusTone = maturedCents > 0 ? 'warning' : isPaid ? 'success' : 'default'
            const canChangeToPaid = maturedCents > 0 && !loading && !updatingKey
            const payoutId = Array.isArray(r.payout_ids) && r.payout_ids.length > 0 ? r.payout_ids[0] : null
            const receipt = showReceiptColumn && payoutId ? receiptByPayoutId[payoutId] : undefined
            const canManageReceipt = showReceiptColumn && allowReceiptManage && isPaid && !!payoutId
            return (
              <Tr key={key}>
                <Td>{monthLabel(r.year, r.month)}</Td>
                <Td>
                  <Badge variant="soft" className="font-mono">
                    {cents(toMatureCents)}
                  </Badge>
                </Td>
                <Td>
                  <Badge
                    variant="soft"
                    className={`font-mono ${isPaid ? 'line-through opacity-70' : ''}`}
                  >
                    {cents(displayMaturedCents)}
                  </Badge>
                </Td>
                <Td>
                  <Badge variant="soft" className="font-mono">
                    {pendingCount} / {maturedCount}
                  </Badge>
                </Td>
                <Td>
                  <Badge variant="soft" className="font-mono">
                    {cents(totalCents)}
                  </Badge>
                </Td>
                <Td>
                  <Badge variant="soft" color={statusTone as any} className="capitalize">
                    {statusLabel}
                  </Badge>
                </Td>
                {showReceiptColumn && (
                  <Td>
                    {allowReceiptManage ? (
                      canManageReceipt ? (
                        receipt ? (
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              onClick={() => {
                                if (receipt.url) window.open(receipt.url, '_blank', 'noopener,noreferrer')
                              }}
                            >
                              Baixar
                            </Button>
                            <Button
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              disabled={receiptUpdatingId === payoutId}
                              onClick={() => handleRemoveReceipt(r)}
                            >
                              Excluir
                            </Button>
                          </div>
                        ) : (
                          <BaseFileUpload
                            accept="image/*,application/pdf"
                            multiple={false}
                            label={
                              <span className="text-xs font-medium text-foreground/80">
                                Enviar comprovante
                              </span>
                            }
                            onFiles={(files) => {
                              const file = Array.isArray(files) && files.length > 0 ? files[0] : null
                              if (!file || receiptUpdatingId) return
                              void handleUploadReceipt(r, file)
                            }}
                          />
                        )
                      ) : (
                        <Badge variant="soft" className="font-mono">
                          -
                        </Badge>
                      )
                    ) : receipt && receipt.url ? (
                      <Button
                        variant="outline"
                        className="h-7 px-2 text-xs"
                        onClick={() => {
                          window.open(receipt.url, '_blank', 'noopener,noreferrer')
                        }}
                      >
                        Baixar
                      </Button>
                    ) : (
                      <Badge variant="soft" className="font-mono">
                        -
                      </Badge>
                    )}
                  </Td>
                )}
                {showActions && (
                  <Td className="text-right">
                    <select
                      className="h-8 rounded-md border border-border bg-background px-2 text-xs"
                      value={isPaid ? 'paid' : maturedCents > 0 ? 'pending' : ''}
                      disabled={isPaid || !canChangeToPaid || updatingKey === key}
                      onChange={(e) => {
                        const v = e.target.value
                        if (v === 'paid' && !isPaid && canChangeToPaid) {
                          handleMarkPaid(r)
                        }
                      }}
                    >
                      <option value="pending">Pendente</option>
                      <option value="paid">Pago</option>
                    </select>
                  </Td>
                )}
              </Tr>
            )
          })}
          {rows.length === 0 && (
            <Tr>
              <Td
                colSpan={6 + (showReceiptColumn ? 1 : 0) + (showActions ? 1 : 0)}
                className="py-6 text-center text-sm text-foreground/70"
              >
                Nenhum dado de comissoes recentes.
              </Td>
            </Tr>
          )}
        </tbody>
      </Table>
    </div>
  )
}
